/* --- START: src/components/NotificationDropdown.js --- */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../storageService';
import './NotificationDropdown.css';

const NotificationDropdown = ({ userId, zenMode }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [activeTab, setActiveTab] = useState('inbox'); 
    const [viewingItem, setViewingItem] = useState(null); // <--- NEW STATE
    const dropdownRef = useRef(null);

    // 1. Derived State
    const unreadItems = useMemo(() => notifications.filter(n => !n.is_read), [notifications]);
    const readItems = useMemo(() => notifications.filter(n => n.is_read), [notifications]);
    const displayItems = activeTab === 'inbox' ? unreadItems : readItems;

    // 2. Data Fetching & Subscription
    useEffect(() => {
        if (!userId) return;
        const fetch = async () => {
            const data = await getNotifications(userId);
            setNotifications(data);
        };
        fetch();
        
        const sub = supabase
            .channel('public:user_notifications')
            .on('postgres_changes', { 
                event: 'INSERT', 
                schema: 'public', 
                table: 'user_notifications', 
                filter: `user_id=eq.${userId}` 
            }, (payload) => {
                setNotifications(prev => [payload.new, ...prev]);
            })
            .subscribe();

        return () => supabase.removeChannel(sub);
    }, [userId]);

    // 3. Click Outside Handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Only close if we aren't viewing a specific item (modal)
            if (!viewingItem && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [viewingItem]);

    // 4. Actions
    const handleItemClick = async (notif) => {
        // Mark as read immediately in UI and DB
        if (!notif.is_read) {
            setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
            await markNotificationRead(notif.id);
        }
        
        // Open the Reader Modal
        setViewingItem(notif);
        // Close the dropdown menu so it doesn't clutter the background
        setIsOpen(false);
    };

    const handleMarkAllRead = async () => {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        await markAllNotificationsRead(userId);
    };

    const getIcon = (type) => {
        switch(type) {
            case 'badge': return 'military_tech';
            case 'streak': return 'local_fire_department';
            case 'arena': return 'trophy';
            default: return 'notifications'; 
        }
    };

    // --- NEW: Render the Reader Modal ---
    const renderReaderModal = () => {
        if (!viewingItem) return null;

        return (
            <div className="reader-modal-overlay fade-in" onClick={() => setViewingItem(null)}>
                <div className="reader-modal-card theme-aware" onClick={e => e.stopPropagation()}>
                    <div className={`reader-header-band ${viewingItem.type}`}>
                        <span className="material-symbols-outlined header-icon">
                            {getIcon(viewingItem.type)}
                        </span>
                    </div>
                    
                    <div className="reader-content">
                        <div className="reader-meta-row">
                            <span className="reader-date">
                                {new Date(viewingItem.created_at).toLocaleDateString(undefined, { 
                                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                                })}
                            </span>
                            <span className="reader-type-tag">{viewingItem.type.toUpperCase()}</span>
                        </div>

                        <h2 className="reader-title">{viewingItem.title}</h2>
                        
                        <div className="reader-message-body">
                            {viewingItem.message}
                        </div>
                    </div>

                    <div className="reader-actions">
                        <button className="btn secondary" onClick={() => setViewingItem(null)}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {/* Render Modal Outside Ref */}
            {renderReaderModal()}

            <div className="notification-wrapper" ref={dropdownRef}>
                <button 
                    className={`header-icon-btn notification-btn ${isOpen ? 'active' : ''}`} 
                    onClick={() => setIsOpen(!isOpen)}
                    title="Notifications"
                >
                    <span className="material-symbols-outlined">
                        {unreadItems.length > 0 ? 'notifications_unread' : 'notifications'}
                    </span>
                    {(unreadItems.length > 0 && !zenMode) && <span className="notification-dot"></span>}
                </button>

                {isOpen && (
                    <div className="notification-menu theme-aware">
                        <div className="notif-header">
                            <button 
                                className={`notif-tab ${activeTab === 'inbox' ? 'active' : ''}`}
                                onClick={() => setActiveTab('inbox')}
                            >
                                Inbox ({unreadItems.length})
                            </button>
                            <button 
                                className={`notif-tab ${activeTab === 'journal' ? 'active' : ''}`}
                                onClick={() => setActiveTab('journal')}
                            >
                                Journal
                            </button>
                        </div>

                        <div className="notif-body">
                            {displayItems.length === 0 ? (
                                <div className="notif-empty">
                                    {activeTab === 'inbox' ? "You're all caught up." : "No history yet."}
                                </div>
                            ) : (
                                <div className="notif-list">
                                    {displayItems.map(n => (
                                        <div 
                                            key={n.id} 
                                            className={`notif-item ${n.is_read ? 'read' : 'unread'}`}
                                            onClick={() => handleItemClick(n)} // Updated Handler
                                        >
                                            <div className={`notif-icon-bubble ${n.type}`}>
                                                <span className="material-symbols-outlined">{getIcon(n.type)}</span>
                                            </div>
                                            <div className="notif-content">
                                                <div className="notif-title">{n.title}</div>
                                                <div className="notif-msg-preview">{n.message}</div> 
                                                <div className="notif-time">
                                                    {new Date(n.created_at).toLocaleDateString()}
                                                </div>
                                            </div>
                                            {!n.is_read && <div className="unread-dot" />}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {activeTab === 'inbox' && unreadItems.length > 0 && (
                            <div className="notif-footer" onClick={handleMarkAllRead}>
                                <span className="material-symbols-outlined">done_all</span> Mark all as read
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default NotificationDropdown;
/* --- END: src/components/NotificationDropdown.js --- */